// The choice kit (ui-map-interface.md §18, R2011): the markup builders of
// every choice control of the game, the hub, the arcade lobby and the
// three labs, in the look the owner picked from mockup round 098.
//
//   - pill group, one value: one round track, a fill that slides under the
//     picked pill (card 02). Several values: separate round pills, each with
//     a tick (card 05).
//   - tab strip: the box of the chat tabs (R1800), the open tab filled with
//     an accent foot.
//   - option list, select box (a popover with a mouse, a sheet under a
//     finger, cards 07 and 08), preview tiles, stepper, tag and button.
//   - `valueChoice` and `filterChoice` pick the form from the count and the
//     label length with the figures of `CHOICE` (`[presentation.lab]`).
//
// A builder returns a string. Every control is a `<button type="button">`
// that carries `data-act` and `data-id` (`pressAttrs`), so each host
// routes a press through its own dispatcher. A text parameter is escaped;
// a parameter named `picture` or `html` is markup the caller built.
//
// The builders read no state, with one exception: `pillGroup` keeps the
// last picked value of each group, so that the fill slides from the old
// pill to the new one when a host rebuilds its markup.
//
// `choice.css` holds the look. Each host imports it once.

import { runeCount } from '../../util/text';
import { choice as S } from '../../gen/strings';
import { CHOICE } from '../../gen/presentation';
import { esc } from '../esc';

// --- the press ----------------------------------------------------------------

/** The data attributes of one press. `arm` marks a two-press control
 *  (R696): the overlay's gate reads `data-arm`. `tab` names the tab of
 *  the overlay's `meta` act (the rank opens STANDINGS). */
export interface Press {
  act: string;
  id?: string;
  key?: string;
  tab?: string;
  arm?: boolean;
  /** More data attributes of the host's own press, by name: `{ clan: '4' }`
   *  writes `data-clan="4"`. */
  data?: Readonly<Record<string, string>>;
}

/** The attributes of a press, with a leading space. */
export function pressAttrs(p: Press): string {
  return ` data-act="${esc(p.act)}"`
    + (p.id === undefined ? '' : ` data-id="${esc(p.id)}"`)
    + (p.key === undefined ? '' : ` data-key="${esc(p.key)}"`)
    + (p.tab === undefined ? '' : ` data-tab="${esc(p.tab)}"`)
    + (p.arm ? ' data-arm="1"' : '')
    + Object.entries(p.data ?? {}).map(([k, v]) => ` data-${esc(k)}="${esc(v)}"`).join('');
}

/** One option of a choice control. */
export interface Choice {
  id: string;
  label: string;
  /** The ink of the option, as a CSS colour (an arcade category's own
   *  colour): the label of a pill, the dot of a select box line. The
   *  picked option keeps its fill, so the state still reads by shape. */
  ink?: string;
  /** A quiet count after the label (the maps of a filter). 0 stands
   *  faint; null or undefined draws none. */
  n?: number | null;
  /** The reader cannot pick the option: it shows and takes no press. */
  locked?: boolean;
  /** The title of the option (why it is locked). */
  title?: string;
  /** A locked option that still takes its press, so the host can say
   *  which gate holds it shut: a disabled button sends no pointer event
   *  (the module tiers of the draft, R701). It wears the locked look and
   *  `aria-disabled`, and is not `disabled`. */
  tell?: boolean;
  /** A key cap after the count: the hotkey that would press the option.
   *  A touch surface hides it by CSS. */
  key?: string;
  /** More data attributes of the option's press, by name (`Press.data`):
   *  a host whose dispatcher reads its own attributes, such as the
   *  casino's `data-op` (R2118). The pill group writes them. */
  data?: Readonly<Record<string, string>>;
}

/** The size of a control. `compact` is the size of a map card and of a
 *  lab: the same form in a denser row (§18). */
export type Size = 'normal' | 'compact';

/** The common options of a control. */
export interface CtlOpts {
  /** The name of the group, for a screen reader. */
  label?: string;
  size?: Size;
}

/** An `aria-label` attribute, or nothing for an empty text. */
export function labelAttr(label: string | undefined): string {
  return label ? ` aria-label="${esc(label)}"` : '';
}

/** A `title` attribute, or nothing for an empty text. */
export function titleAttr(text: string | undefined): string {
  return text ? ` title="${esc(text)}"` : '';
}

/** The ink of an option as the `--ch-ink` of its pill, or nothing. */
function inkStyle(ink: string | undefined): string {
  return ink ? ` style="--ch-ink:${esc(ink)}"` : '';
}

/** The NOW tag of the value in force, or nothing. */
function nowMark(id: string, now: string | undefined): string {
  return id === now ? `<i class="ch-now">${S.now}</i>` : '';
}

function sizeClass(size: Size | undefined): string {
  return size === 'compact' ? ' compact' : '';
}

/** The quiet count of an option, or ''. */
function countMark(n: number | null | undefined): string {
  return n === undefined || n === null ? '' : `<em class="ch-n">${n}</em>`;
}

function keyMark(key: string | undefined): string {
  return key ? `<kbd class="ck-key">${esc(key)}</kbd>` : '';
}

/** The disabled attributes of a locked option, or ''. */
function lockAttrs(c: Choice): string {
  if (!c.locked) return titleAttr(c.title);
  return `${c.tell ? '' : ' disabled'} aria-disabled="true"${titleAttr(c.title ?? S.locked_title)}`;
}

// --- the pill group -----------------------------------------------------------

/** The last picked value of each pill group, by its act and its options,
 *  so the fill slides when a host rebuilds the group with a new value. It
 *  keeps the `LAST_PICK_CAP` groups drawn last; the group drawn longest
 *  ago goes first (R3028). */
const lastPick = new Map<string, string>();
const LAST_PICK_CAP = 64;

/**
 * The pill group of one value (card 02): one round track, one width a
 * pill, and a fill under the picked pill that slides from the pill picked
 * before. The track carries the count and the place of the fill as
 * `--n` and `--i` (and `--from` while it slides). `role="radiogroup"`,
 * and each pill a `role="radio"` with `aria-checked`.
 */
export function pillGroup(list: readonly Choice[], chosen: string, act: string, o: CtlOpts = {}): string {
  const at = list.findIndex((c) => c.id === chosen);
  const key = `${act}|${list.map((c) => c.id).join(',')}`;
  const before = lastPick.get(key);
  // A delete before the set moves the group to the end of the order.
  lastPick.delete(key);
  lastPick.set(key, chosen);
  if (lastPick.size > LAST_PICK_CAP) lastPick.delete(lastPick.keys().next().value!);
  const from = before === undefined || before === chosen ? -1 : list.findIndex((c) => c.id === before);
  const vars = `--n:${list.length}`
    + (at < 0 ? '' : `;--i:${at}`)
    + (at < 0 || from < 0 ? '' : `;--from:${from}`);
  const cls = `ch-pg${sizeClass(o.size)}${at < 0 ? ' none' : ''}${from < 0 ? '' : ' slide'}${allShort(list) ? ' short' : ''}`;
  return `<span class="${cls}" role="radiogroup"${labelAttr(o.label)} style="${vars}">`
    + list.map((c) => `<button type="button" class="ch-pl${c.id === chosen ? ' on' : ''}" role="radio"`
      + ` aria-checked="${c.id === chosen}"${pressAttrs({ act, id: c.id, data: c.data })}${lockAttrs(c)}`
      + `${inkStyle(c.ink)}>${esc(c.label)}${countMark(c.n)}${keyMark(c.key)}</button>`).join('')
    + '</span>';
}

/**
 * The pill group of several values (card 05): separate round pills, each
 * with a tick box, each a press of its own with `aria-pressed`. A press
 * carries `act` and the option as `data-id`; the host toggles it. `alt`
 * names the picked options of a second kind (a cup reserve), which take a
 * dashed rim. `wrap` lets a roster of names wrap to more rows.
 */
export function pillMulti(list: readonly Choice[], picked: Iterable<string>, act: string,
  o: CtlOpts & { alt?: Iterable<string>; wrap?: boolean } = {}): string {
  const alt = new Set(o.alt ?? []);
  const on = new Set([...picked, ...alt]);
  return `<span class="ch-pm${sizeClass(o.size)}${o.wrap ? ' wrap' : ''}" role="group"${labelAttr(o.label)}>`
    + list.map((c) => `<button type="button" class="ch-pl${on.has(c.id) ? ' on' : ''}${alt.has(c.id) ? ' alt' : ''}"`
      + ` aria-pressed="${on.has(c.id)}"${pressAttrs({ act, id: c.id })}${lockAttrs(c)}`
      + `${inkStyle(c.ink)}><i class="ch-tick" aria-hidden="true"></i>`
      + `${esc(c.label)}${countMark(c.n)}</button>`).join('')
    + '</span>';
}

// --- the tab strip ------------------------------------------------------------

/** One tab of a tab strip. */
export interface Tab extends Choice {
  /** The unread lines of the tab: a light count, none at 0. */
  unread?: number;
  /** Trusted markup of a mark after the word (an SVG the caller built in
   *  the current ink): the person mark of a team-size tab of the arcade
   *  lobby on a telephone (R2074). */
  mark?: string;
  /** The accessible name of the tab, where the word is a short form of
   *  it (R2074). */
  aria?: string;
}

/**
 * The tab strip (§18.3): one box, joined tabs of one width, the open tab
 * filled with a 2 px accent foot. A tab may carry a quiet count (`n`) and
 * a light count of unread lines, and a dot of its own ink before the word
 * (an arcade folder). `role="tablist"`, and each tab a
 * `role="tab"` with `aria-selected`.
 *
 * A tab strip never scrolls. `scroll` is for a section bar alone: the
 * navigation between the panels of a lab on a telephone, which scrolls
 * sideways when its tabs do not fit, as the game tab bar does (R2027).
 */
export function tabStrip(list: readonly Tab[], open: string, act: string,
  o: CtlOpts & { scroll?: boolean } = {}): string {
  return `<span class="ch-ts${sizeClass(o.size)}${allShort(list) ? ' short' : ''}${o.scroll ? ' scroll' : ''}"`
    + ` role="tablist"${labelAttr(o.label)}>`
    + list.map((t) => `<button type="button" class="ch-tab${t.id === open ? ' on' : ''}${t.n === 0 ? ' z' : ''}"`
      + ` role="tab" aria-selected="${t.id === open}"${pressAttrs({ act, id: t.id })}${lockAttrs(t)}${labelAttr(t.aria)}>`
      + `${t.ink ? dot(t.ink) : ''}<span class="ch-tl">${esc(t.label)}</span>${t.mark ?? ''}${countMark(t.n)}`
      + (t.unread !== undefined && t.unread > 0 ? `<span class="ch-lc">${t.unread}</span>` : '')
      + '</button>').join('')
    + '</span>';
}

// --- the option list ----------------------------------------------------------

/** The inline confirm strip under a picked line. */
export interface Confirm {
  text: string;
  go: Press & { label: string };
  no: Press & { label: string };
}

/**
 * The option list (§18.3): one line a value with a radio mark. `now` tags
 * the value in force, and a `confirm` strip opens under the picked line
 * when the pick waits for a second word (the language, which reloads).
 */
export function optionList(list: readonly Choice[], chosen: string, act: string,
  o: CtlOpts & { now?: string; confirm?: Confirm } = {}): string {
  return `<div class="ch-ol${sizeClass(o.size)}" role="radiogroup"${labelAttr(o.label)}>`
    + list.map((c) => {
      const pick = c.id === chosen;
      const open = pick && o.confirm !== undefined;
      return `<button type="button" class="ch-oi${pick ? ' on' : ''}${open ? ' open' : ''}" role="radio"`
        + ` aria-checked="${pick}"${pressAttrs({ act, id: c.id })}${lockAttrs(c)}>`
        + `<span class="ch-rm" aria-hidden="true"></span>${c.ink ? dot(c.ink) : ''}<b>${esc(c.label)}</b>`
        + countMark(c.n) + nowMark(c.id, o.now) + '</button>'
        + (open && o.confirm ? confirmStrip(o.confirm) : '');
    }).join('') + '</div>';
}

/** The confirm strip of a pick that waits: the sentence, then GO and NO.
 *  The strip hangs from the picked line of an option list; `free` draws
 *  it as a box of its own under a control that has no line (the tiles). */
export function confirmStrip(c: Confirm, o: { free?: boolean } = {}): string {
  return `<div class="ch-conf${o.free ? ' free' : ''}"><span>${esc(c.text)}</span><span class="ch-confb">`
    + button(c.go.label, c.go, { variant: 'primary' }) + button(c.no.label, c.no) + '</span></div>';
}

/** The ink dot of an option. */
function dot(ink: string): string {
  return `<span class="ch-dot" style="background:${esc(ink)}" aria-hidden="true"></span>`;
}

// --- the select box -----------------------------------------------------------

/** The form of the list of a select box: `auto` is a popover with a mouse
 *  and a sheet under a finger (`pointer: coarse`). */
export type SelectForm = 'auto' | 'pop' | 'sheet';

/** The options of a select box. */
export interface SelectOpts extends CtlOpts {
  /** A unique name of the box on its page; the act is the default. The
   *  popover id and the anchor name are made from it. */
  uid?: string;
  /** The name of the choice over the sheet; `label` is the default. */
  title?: string;
  /** The quiet line at the foot of the list. */
  note?: string;
  /** The form of the list; `auto` is the default. */
  form?: SelectForm;
  /** The confirm strip under the field (a pick that waits). */
  confirm?: Confirm;
  /** The value in force, which a NOW tag marks. */
  now?: string;
  /** The faint word of the field while no option is picked (a report
   *  that waits for its reason). */
  placeholder?: string;
}

/** A name safe for an id and for a CSS dashed name. */
function slug(text: string): string {
  return text.replace(/[^A-Za-z0-9_-]/g, '_');
}

/**
 * The select box (§18.3): a field that names the picked value, and the
 * list of every option in an HTML `popover`, so a press opens and closes
 * it with no call to the host, and a pick, a press outside and Escape
 * close it. With a mouse the list is a popover under the field (card 08);
 * under a finger it is a bottom sheet with a grip, the title and ✕ (card
 * 07). An option may carry a dot of its ink, a count and a locked state.
 * Each option is a `role="option"` with `aria-selected`, and carries
 * `act` and its id.
 */
export function selectBox(list: readonly Choice[], chosen: string, act: string, o: SelectOpts = {}): string {
  const id = `ch-sb-${slug(o.uid ?? act)}`;
  const pick = list.find((c) => c.id === chosen);
  const title = o.title ?? o.label ?? '';
  const form = o.form === undefined || o.form === 'auto' ? '' : ` ${o.form}`;
  const field = `<button type="button" class="ch-sbf" popovertarget="${id}" aria-haspopup="listbox"`
    + `${labelAttr(o.label)} style="anchor-name:--${id}">`
    + `${pick?.ink ? dot(pick.ink) : ''}`
    + (pick === undefined && o.placeholder ? `<span class="ch-v ch-ph">${esc(o.placeholder)}</span>`
      : `<span class="ch-v">${esc(pick?.label ?? '')}</span>`)
    + `${countMark(pick?.n)}<span class="ch-car" aria-hidden="true">▾</span></button>`;
  const lines = list.map((c) => {
    const on = c.id === chosen;
    return `<button type="button" class="ch-sbi${on ? ' on' : ''}${c.n === 0 ? ' z' : ''}" role="option"`
      + ` aria-selected="${on}"${pressAttrs({ act, id: c.id })}${lockAttrs(c)}`
      + ` popovertarget="${id}" popovertargetaction="hide">`
      + `<span class="ch-rm" aria-hidden="true"></span>${c.ink ? dot(c.ink) : ''}<b>${esc(c.label)}</b>`
      + countMark(c.n) + nowMark(c.id, o.now) + '</button>';
  }).join('');
  const pop = `<div class="ch-sbp" id="${id}" popover="auto" style="position-anchor:--${id}"${labelAttr(title)}>`
    + '<span class="ch-grip" aria-hidden="true"></span>'
    + `<div class="ch-sbh"><b>${esc(title)}</b><button type="button" class="ch-sbx" popovertarget="${id}"`
    + ` popovertargetaction="hide" aria-label="${esc(S.close_title)}" title="${esc(S.close_title)}">✕</button></div>`
    + `<div class="ch-sbl" role="listbox"${labelAttr(title)}>${lines}</div>`
    + (o.note ? `<p class="ch-sbn">${esc(o.note)}</p>` : '') + '</div>';
  return `<span class="ch-sb${sizeClass(o.size)}${form}">${field}${pop}</span>`
    + (o.confirm ? confirmStrip(o.confirm) : '');
}

// --- preview tiles ------------------------------------------------------------

/** One preview tile: an option with the picture the caller drew. */
export interface Tile extends Choice {
  /** The picture over the word, as markup (an `aria-hidden` SVG). */
  picture: string;
  /** A small word after the label: the tier a locked look needs. */
  mark?: string;
  /** A quiet second line under the word. */
  sub?: string;
}

/**
 * Preview tiles (§18.3): a grid of equal tiles, each a picture over its
 * word, for a choice whose difference is a look. The picked tile wears the
 * accent rim and its word a 2 px foot and a filled radio mark. `cols`
 * fixes the columns; with none, a list of up to four takes one row and a
 * longer list fills the row with tiles of at least the tile width.
 *
 * The word stands left in its own span `ch-tn`, which ends in an ellipsis
 * when the tile is too narrow (R2043). The full word stays the text of
 * the press, so a screen reader reads all of it, and a tile with no title
 * of its own takes the word as its title, so a mouse reads it on hover.
 * `named` is for a group whose names tell the looks apart (the UI palettes
 * of MODDING): the row takes its whole width, a tile is wide enough for
 * the longest word, and a name wraps to a second line and never ends in an
 * ellipsis (R2169).
 */
export function previewTiles(list: readonly Tile[], chosen: string, act: string,
  o: CtlOpts & { cols?: number; named?: boolean } = {}): string {
  const cols = o.cols ?? (list.length <= CHOICE.segMax ? list.length : 0);
  return `<span class="ch-tiles${sizeClass(o.size)}${cols > 0 ? '' : ' fill'}${o.named ? ' named' : ''}"`
    + ` role="radiogroup"${labelAttr(o.label)}`
    + `${cols > 0 ? ` style="--cols:${cols}"` : ''}>`
    + list.map((t) => `<button type="button" class="ch-tile${t.id === chosen ? ' on' : ''}" role="radio"`
      + ` aria-checked="${t.id === chosen}"${pressAttrs({ act, id: t.id, data: t.data })}`
      + `${lockAttrs(t.title === undefined && !t.locked ? { ...t, title: t.label } : t)}>`
      + `${t.picture}<b><span class="ch-tn">${esc(t.label)}</span>`
      + `${t.mark ? `<small class="ch-mk">${esc(t.mark)}</small>` : ''}</b>`
      + `${t.sub ? `<i class="ch-sub">${esc(t.sub)}</i>` : ''}</button>`).join('')
    + '</span>';
}

// --- the stepper --------------------------------------------------------------

/**
 * The stepper (R1776): ‹ VALUE ›. Each arrow carries `act` and its step as
 * `data-id` (-1 or 1). The value has a fixed width, so no arrow moves
 * when the value changes, and a long value ends in an ellipsis. `still`
 * holds both arrows while the list holds one value alone; `noPrev` and
 * `noNext` hold one arrow at the end of a range (the LOADOUT sheet, R2031).
 * `id` names the object of one stepper among several: each arrow then
 * carries "id:step", the overlay's "id:n" (R2011). `count` narrows the
 * value to a count of a few digits, so three steppers and their labels fit
 * one card row on a 390 px telephone (R2031).
 */
export function stepper(value: string, act: string,
  o: {
    label?: string; still?: boolean; prev: string; next: string; size?: Size;
    id?: string; noPrev?: boolean; noNext?: boolean; count?: boolean;
  }): string {
  const arrow = (dir: number, glyph: string, name: string, stop: boolean | undefined): string =>
    `<button type="button" class="ch-arr"${pressAttrs({ act, id: o.id === undefined ? String(dir) : `${o.id}:${dir}` })}`
    + ` aria-label="${esc(name)}"${o.still || stop ? ' disabled' : ''}>${glyph}</button>`;
  return `<span class="ch-step${sizeClass(o.size)}${o.count ? ' count' : ''}" role="group"${labelAttr(o.label)}>`
    + arrow(-1, '‹', o.prev, o.noPrev)
    + `<span class="ch-val" aria-live="polite" title="${esc(value)}">${esc(value)}</span>`
    + arrow(1, '›', o.next, o.noNext)
    + '</span>';
}

// --- the tag and the button ---------------------------------------------------

/** The tone of a tag. `bad` is a refusal or a danger, `amber` a state
 *  that waits (a check in progress). */
export type TagTone = 'plain' | 'accent' | 'gold' | 'violet' | 'good' | 'bad' | 'amber';

/** The text of a tag: plain text, which the kit escapes, or `{ html }`,
 *  markup the caller built (a live clock in a `<b>`). */
export type TagText = string | { readonly html: string };

/** A tag (§18.3): a small outline box that shows a state. It is never a
 *  press, so it has no hover and no press state. */
export function tag(text: TagText, tone: TagTone = 'plain', title = ''): string {
  const body = typeof text === 'string' ? esc(text) : text.html;
  return `<em class="ch-tag${tone === 'plain' ? '' : ` ${tone}`}"${titleAttr(title)}>${body}</em>`;
}

/** The variant of a button. */
export type ButtonVariant = 'plain' | 'primary' | 'gold' | 'danger';

const VARIANT_CLASS: Record<ButtonVariant, string> = { plain: '', primary: ' pri', gold: ' gold', danger: ' bad' };

/** The options of a button. */
export interface ButtonOpts {
  variant?: ButtonVariant;
  size?: Size;
  /** The lit button: the panel or the page it opens stands. */
  on?: boolean;
  disabled?: boolean;
  title?: string;
  /** The button takes the whole width of its row. */
  wide?: boolean;
  /** Markup before the label (a line icon). */
  html?: string;
  /** More classes for the host's layout. */
  cls?: string;
  /** The armed face of an armed control (R2069): the word that names the
   *  act. The button then holds both faces in one grid cell (`armFaces`). */
  armFace?: string;
  /** The armed control is armed: it shows `armFace` and wears `armed`. */
  armed?: boolean;
}

/**
 * The two faces of an armed control (§18.3, R2059, R2067, R2069): the word
 * at rest and the armed word that names the act, such as SCRAP?. Both
 * stand in one grid cell, so the press takes the width of the wider face
 * and keeps it when it arms, and no neighbour moves under the pointer.
 * `on` shows the armed face. The sheet hides the face that is not in
 * force, and a screen reader skips it. Both faces are trusted markup.
 */
export function armFaces(restHtml: string, armHtml: string, on: boolean): string {
  return `<span class="ch-faces${on ? ' on' : ''}"><span class="face rest">${restHtml}</span>`
    + `<span class="face arm">${armHtml}</span></span>`;
}

/**
 * A button (§18.3): every action and every way to another panel. It is
 * rectangular, with the `--r-button` corner, in one of four variants:
 * plain, primary, gold and danger. Each variant shows a hover, a press
 * and a focus ring.
 */
export function button(label: string, press: Press, o: ButtonOpts = {}): string {
  const cls = `ch-btn${VARIANT_CLASS[o.variant ?? 'plain']}${sizeClass(o.size)}${o.on ? ' on' : ''}`
    + `${o.wide ? ' wide' : ''}${o.armed ? ' armed' : ''}${o.cls ? ` ${o.cls}` : ''}`;
  const word = o.armFace === undefined ? esc(label) : armFaces(esc(label), esc(o.armFace), o.armed === true);
  return `<button type="button" class="${cls}"${pressAttrs(press)}${titleAttr(o.title)}`
    + `${o.on ? ' aria-current="true"' : ''}${o.disabled ? ' disabled' : ''}>${o.html ?? ''}${word}</button>`;
}

/**
 * A button that is a link (§18.3): the way to another page, as an anchor,
 * so a press opens the page and the browser keeps its own link presses.
 * It takes the look of `button`; `blank` opens the page in a tab of its
 * own. A link has no disabled state.
 */
export function linkButton(label: string, href: string,
  o: Omit<ButtonOpts, 'disabled' | 'on'> & { blank?: boolean } = {}): string {
  const cls = `ch-btn${VARIANT_CLASS[o.variant ?? 'plain']}${sizeClass(o.size)}`
    + `${o.wide ? ' wide' : ''}${o.cls ? ` ${o.cls}` : ''}`;
  return `<a class="${cls}" href="${esc(href)}"${o.blank ? ' target="_blank" rel="noreferrer"' : ''}`
    + `${titleAttr(o.title)}>${o.html ?? ''}${esc(label)}</a>`;
}

// --- the choosers -------------------------------------------------------------

/** True when every label of the list has one or two characters (`1`, `L4`). */
export function allShort(list: readonly { label: string }[]): boolean {
  return list.length > 0 && list.every((c) => runeCount(c.label) <= 2);
}

/** The forms of a value. */
export type ValueForm = 'pills' | 'list' | 'select';
/** The forms of a filter. */
export type FilterForm = 'tabs' | 'select';

/** What decides the form of a choice. */
export interface FormRule {
  /** The list has no upper bound: it takes the select box at any count. */
  unbounded?: boolean;
  /** The smallest form of a value: `list` keeps an option list where the
   *  count would give a pill group (the language). */
  floor?: 'list';
}

/** True when the count fits a pill group or a tab strip. */
function fitsSeg(count: number, short: boolean): boolean {
  return count <= CHOICE.segMax || (short && count <= CHOICE.segMaxShort);
}

/**
 * The form of a value of `count` options (§18.2): a pill group up to
 * `choice_seg_max` (or `choice_seg_max_short` for short labels), an
 * option list up to `choice_list_max`, a select box past it or with no
 * upper bound.
 */
export function valueForm(count: number, short: boolean, r: FormRule = {}): ValueForm {
  if (r.unbounded) return 'select';
  if (r.floor !== 'list' && fitsSeg(count, short)) return 'pills';
  return count <= CHOICE.listMax ? 'list' : 'select';
}

/** The form of a filter of `count` options (§18.2): a tab strip up to
 *  `choice_seg_max` (or `choice_seg_max_short` for short labels), else a
 *  select box. */
export function filterForm(count: number, short: boolean, r: FormRule = {}): FilterForm {
  if (r.unbounded) return 'select';
  return fitsSeg(count, short) ? 'tabs' : 'select';
}

/** The options of a chooser. */
export interface ChooserOpts extends FormRule, CtlOpts {
  list: readonly Choice[];
  chosen: string;
  act: string;
  /** The value in force (an option list or a select box tags it NOW). */
  now?: string;
  /** The confirm strip of a pick that waits. */
  confirm?: Confirm;
  /** The select box: its unique name, its title, its foot line, its form,
   *  and the word of its field while no option is picked. */
  uid?: string;
  title?: string;
  note?: string;
  form?: SelectForm;
  placeholder?: string;
}

/** The control of a value, in the form its count gives (§18.2). */
export function valueChoice(o: ChooserOpts): string {
  const form = valueForm(o.list.length, allShort(o.list), o);
  if (form === 'pills') return pillGroup(o.list, o.chosen, o.act, o);
  if (form === 'list') return optionList(o.list, o.chosen, o.act, o);
  return selectBox(o.list, o.chosen, o.act, o);
}

/** The control of a filter, in the form its count gives (§18.2). */
export function filterChoice(o: ChooserOpts & { list: readonly Tab[] }): string {
  return filterForm(o.list.length, allShort(o.list), o) === 'tabs'
    ? tabStrip(o.list, o.chosen, o.act, o)
    : selectBox(o.list, o.chosen, o.act, o);
}
